import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Button, EmptyState } from '@bb/ui';
import type { AdminApi, DebugLogSummary, EventLogFilters } from '../../api/client.ts';

const time = (iso: string) => iso.slice(11, 19);

function RawEntry({ environmentId, id }: { environmentId: string; id: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const entry = useQuery({ queryKey: ['event-log-entry', environmentId, id], queryFn: () => api.eventLogEntry(environmentId, id) });
  if (entry.isPending) return <p aria-busy="true">Loading…</p>;
  if (entry.isError) return <p role="alert">The entry could not be loaded.</p>;
  const failed = (entry.data.results as Array<{ status?: string; code?: string; operationId?: string }>).filter((r) => r.status === 'permanent_error' || r.status === 'retryable_error');
  return (
    <div className="event-log__raw">
      {failed.length > 0 && (
        <ul aria-label="Rejected operations" className="event-log__errors">
          {failed.map((r) => (
            <li key={r.operationId} className="tone-danger">
              {r.status}: <code>{r.code}</code>
            </li>
          ))}
        </ul>
      )}
      {entry.data.maskedPaths.length > 0 && <p className="hint">Personal data is hidden ({entry.data.maskedPaths.length} values); seeing it needs pii.view.</p>}
      {entry.data.truncated && <p className="hint">The request was over 64 KiB; only the first operations are kept.</p>}
      <pre aria-label="Request">{JSON.stringify(entry.data.request, null, 2)}</pre>
      <pre aria-label="Results">{JSON.stringify(entry.data.results, null, 2)}</pre>
    </div>
  );
}

function Row({ entry, environmentId }: { entry: DebugLogSummary; environmentId: string }) {
  const [open, setOpen] = useState(false);
  const status = entry.httpStatus !== 200 ? `HTTP ${entry.httpStatus} ${entry.problemCode ?? ''}` : entry.errorCount ? `${entry.errorCount} of ${entry.operationCount} rejected` : `${entry.operationCount} accepted`;
  return (
    <li className={`event-log__row${entry.errorCount || entry.httpStatus !== 200 ? ' event-log__row--error' : ''}`}>
      <div className="event-log__line">
        <span>{time(entry.receivedAt)}</span> <span className="tag tag--neutral">{entry.surface === 'sdk' ? 'SDK' : 'Server'}</span> <strong>{entry.externalId ?? entry.installationId ?? '—'}</strong>{' '}
        {entry.surface === 'sdk' && <span className="hint">app {entry.appVersion} · SDK {entry.sdkVersion}</span>} <span>{entry.operations.join(', ')}</span>{' '}
        <span className={entry.errorCount || entry.httpStatus !== 200 ? 'tone-danger' : 'tone-success'}>{status}</span>
        {entry.codes.map((c) => (
          <code key={c} className="tone-danger">
            {' '}
            {c}
          </code>
        ))}{' '}
        <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? 'Hide raw JSON' : 'Show raw JSON'}
        </Button>
      </div>
      {open && <RawEntry environmentId={environmentId} id={entry.id} />}
    </li>
  );
}

/**
 * Live Event User Log (BB-35): the latest captured batches, then new ones every `pollMs` (2 s) after the last seen id;
 * paused, it stops asking. Each row expands to the raw request with rejected operations highlighted.
 */
export function EventLogStream({ environmentId, filters, pollMs = 2_000, emptyText }: { environmentId: string; filters: Omit<EventLogFilters, 'after'>; pollMs?: number; emptyText?: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [entries, setEntries] = useState<DebugLogSummary[]>([]);
  const [paused, setPaused] = useState(false);
  const last = useRef<string | null>(null);
  const key = JSON.stringify(filters);
  const initial = useQuery({ queryKey: ['event-log', environmentId, key], queryFn: () => api.eventLog(environmentId, filters) });
  useEffect(() => {
    if (!initial.data) return;
    setEntries(initial.data.items);
    last.current = initial.data.lastId;
  }, [initial.data]);
  useEffect(() => {
    if (paused || !initial.data) return undefined;
    let busy = false;
    const timer = setInterval(() => {
      if (busy) return;
      busy = true;
      api
        .eventLog(environmentId, { ...filters, ...(last.current ? { after: last.current } : {}) })
        .then((page) => {
          if (page.items.length) setEntries((current) => [...current, ...page.items].slice(-500));
          last.current = page.lastId ?? last.current;
        })
        .catch(() => undefined)
        .finally(() => {
          busy = false;
        });
    }, pollMs);
    return () => clearInterval(timer);
    // `key` stands for `filters`.
  }, [api, environmentId, key, paused, pollMs, initial.data]);

  return (
    <section aria-label="Live log" className="event-log">
      <div className="filters">
        <Button size="sm" variant={paused ? 'primary' : 'secondary'} onClick={() => setPaused(!paused)}>
          {paused ? 'Resume' : 'Pause'}
        </Button>{' '}
        <span role="status" className="hint">
          {paused ? 'Paused' : `Live · updates every ${pollMs / 1000} s`}
        </span>
      </div>
      {initial.isPending ? (
        <p aria-busy="true">Loading the log…</p>
      ) : initial.isError ? (
        <p role="alert">The log could not be loaded.</p>
      ) : entries.length === 0 ? (
        <EmptyState title="Nothing captured yet" description={emptyText ?? 'No test profiles yet — add one to an internal group with Event log, or start an SDK debug session.'} />
      ) : (
        <ol aria-label="Captured batches" className="event-log__list">
          {[...entries].reverse().map((e) => (
            <Row key={e.id} entry={e} environmentId={environmentId} />
          ))}
        </ol>
      )}
    </section>
  );
}
